import { ReactNode, useCallback, useMemo } from 'react';
import { useFeatureFlag } from '@mews/framework';
import { useRepositoryQuery } from '@mews-commander/data-access';
import { FeatureFlag } from '@mews-commander/enums';
import { WithScope } from '@mews-commander/types';
import { BillingAutomationChangesType, useCommanderSelector } from '@mews-commander/utils';
import {
    getPreselectedCompanyId,
    getPreselectedTravelAgencyId,
} from '../../../reservationModule/components/PropertiesTab/selectors';
import { getRateFormInitialValues } from '../../../reservationModule/components/PricingSection/selectors';
import { useServicesSuspenseQuery } from '../form/steps/itemsAndConditions/queries';
import { getOrigin, mapReservationRoutingDetailsToConfirmationPanel, reservationHasRoutingRuleChanges } from './utils';
import { RoutingRuleChangeConfirmationPanel } from './RoutingRuleChangeConfirmationPanel';

interface RoutingRuleChangesConfirmationResult {
    validate: () => { dialog?: ReactNode } | null;
    isLoading: boolean;
}

interface RoutingRuleChangesConfirmationParams extends WithScope {
    reservationIds: string[];
    partnerCompanyId?: string | null;
    travelAgencyId?: string | null;
    rateId?: string | null;
    enabled: boolean;
    onSubmit: (args: { changes: BillingAutomationChangesType }) => void;
    onCancel: () => void;
}

export const useRoutingRuleChangesConfirmation = ({
    reservationIds,
    partnerCompanyId,
    travelAgencyId,
    rateId,
    enabled,
    onSubmit,
    onCancel,
    scope,
}: RoutingRuleChangesConfirmationParams): RoutingRuleChangesConfirmationResult => {
    const routingRulesSlice2Enabled = useFeatureFlag(FeatureFlag.routingRulesSlice2Enabled);
    const newRoutingRulesOnAddEnabled = useFeatureFlag(FeatureFlag.newRoutingRulesOnAddEnabled);
    const currentTravelAgencyId = useCommanderSelector((state) => getPreselectedTravelAgencyId(state, { scope }));
    const currentCompanyId = useCommanderSelector((state) => getPreselectedCompanyId(state, { scope }));
    const { rateId: currentRateId } = useCommanderSelector((state) => getRateFormInitialValues(state, { scope }));

    const { data: allServices } = useServicesSuspenseQuery();

    const { data, isLoading } = useRepositoryQuery(
        'routingRules',
        'getDetailsOnReservationUpdate',
        {
            reservationIds,
            partnerCompanyId: partnerCompanyId === undefined ? currentCompanyId : partnerCompanyId,
            travelAgencyId: travelAgencyId === undefined ? currentTravelAgencyId : travelAgencyId,
            rateId: rateId === undefined ? currentRateId : rateId,
        },
        {
            enabled: routingRulesSlice2Enabled && newRoutingRulesOnAddEnabled && enabled,
            select: (result) => ({
                hasChanges: reservationHasRoutingRuleChanges(result.reservationRoutingDetails),
                data: mapReservationRoutingDetailsToConfirmationPanel(result.reservationRoutingDetails, allServices),
            }),
        }
    );

    const origin = getOrigin({
        partnerCompanyId,
        travelAgencyId,
    });
    const validate = useCallback(() => {
        if (routingRulesSlice2Enabled && enabled && data?.hasChanges) {
            const bindOnSubmit = () => {
                onSubmit({
                    changes: data.data.typeOfChange,
                });
            };
            return {
                dialog: (
                    <RoutingRuleChangeConfirmationPanel
                        onClose={onCancel}
                        onSubmit={bindOnSubmit}
                        opened
                        {...data.data}
                        origin={origin}
                    />
                ),
            };
        }
        return null;
    }, [data, onCancel, enabled, onSubmit, routingRulesSlice2Enabled, origin]);

    return useMemo(
        () => ({
            isLoading,
            validate,
        }),
        [isLoading, validate]
    );
};
